import { getPositionFromIndex, createAnchor, getOverlappingAnchors, isAlreadyAnchored, isRejectedByLivePosition } from "../components/AutoSuggestion";
import { findCommandorNot, capitalizeInsideBraces, LineCommented, isCommented, isLineCommented, findTabularEnvs, parseColumnSpec, createGranularAnchors, numberToWords, makeAnchorEmitter, buildStyleContext } from "../components/ReusableFunctions";
import { getInsideTextofBraces } from "../components/ReusableFunctions";
import { changeToSentenceCase } from "../components/TextCases";
import { US_TO_UK, } from '../Utils/US_TO_UK_DATA';


const UK_TO_US = Object.fromEntries(
    Object.entries(US_TO_UK).map(([k, v]) => [v, k])
);


export const commonStyles = {
    dashclassification: {
        name: "Normalize Dash Between Numbers",
        description: "Change dash, en-dash, em-dash with spaces to a single '-' between numbers",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const pattern = /(\d+)\s*[-–—]\s*(\d+)/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}-${match[2]}`;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
}
export const eplStyles = {

    RemoveHiddenText: {
        name: "Remove Hidden Text start with %",
        description: "Remove commented/hidden text from LaTeX",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const lines = texFile.split('\n');
            let index = 0;

            lines.forEach((line, lineNum) => {
                const lineStart = index;
                index += line.length + 1; // +1 for \n

                // Find % that is not escaped with \
                const commentIdx = line.search(/(?<!\\)%/);
                if (commentIdx === -1) return;

                const beforeComment = line.slice(0, commentIdx);
                const isFullLine = beforeComment.trim() === '';

                let fullMatch, matchIndex, extraStyleInfo;

                if (isFullLine) {
                    // ── Full line — anchor just the line text (no \n) ──
                    // removeType:'fullLine' tells applyAcceptToEditor to delete the whole line
                    fullMatch = line;
                    matchIndex = lineStart;
                    extraStyleInfo = { removeType: 'fullLine' };
                } else {
                    // ── Partial comment — anchor only the % and everything after ──
                    // Trim trailing spaces before the % too
                    const trimmedBefore = beforeComment.trimEnd();
                    const extraSpaces = beforeComment.length - trimmedBefore.length;
                    fullMatch = line.slice(commentIdx - extraSpaces); // spaces + %comment
                    matchIndex = lineStart + commentIdx - extraSpaces;
                    extraStyleInfo = { removeType: 'partial' };
                }

                const position = getPositionFromIndex(texFile, matchIndex, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }

                createAnchor(session, Range, position,
                    fullMatch, '',
                    this.markerClass, this.name, this.description,
                    anchorlist, extraStyleInfo);
            });

            return staleIds;
        }
    },

    ThousandSeparator: {
        name: "Thousand separator",
        description: "Add commas to numbers for thousands separation, skip 4-digit numbers",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/commaInNumberBefore.png",
        afterImage: "/images/description/commaInNumberAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const { segments } = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,

                // ── Scope ────────────────────────────────────────────────────────
                // Only inside \begin{document}…\end{document}
                scope: { wholeDocument: true },

                // ── Math exclusion ────────────────────────────────────────────────
                // Skip ALL math zones — numbers in equations are never touched.
                excludeMath: true,
                excludeAllCommands: true,

                // ── Environment exclusion ─────────────────────────────────────────
                // Skip entire table / figure / longtable / sidewaystable blocks
                // including EVERY inner command inside them (\tabular, \hline, etc.)
                // Also skip verbatim / lstlisting / minted code blocks.
                excludeOnlyEnvironments: [
                    'table', 'tabular', 'figure', 'longtable', 'sidewaystable',
                    'verbatim', 'lstlisting', 'minted', 'thebibliography'
                ],
                // excludeEnvironments: ['itemize'],

                // ── Inner-command exclusion ───────────────────────────────────────
                // Skip \cmd{…} whose content is an identifier, not a quantity.
                // excludeInner: [
                //     'label', 'ref', 'eqref', 'cite', 'bibitem',
                //     'year', 'pubdate', 'jryear', 'jrdoi',
                //     'url', 'href', 'hyperref', 'dispmath'
                // ],
            });

            // Match integers of 5 or more digits (4-digit numbers are excluded per spec).
            // \b word-boundary guards prevent matching inside hyphenated text or identifiers.
            // The negative lookbehind/ahead for commas prevents re-flagging already-formatted numbers.
            // const numberPat = /\b(\d{4,})\b/g;
            const numberPat = /(?<!year)(?<=\s+)(\d{4,})/g;

            for (const seg of segments) {
                numberPat.lastIndex = 0;
                let m;
                while ((m = numberPat.exec(seg.text)) !== null) {
                    const original = m[0];

                    if (original < 2030 && original > 1900) continue;

                    // Guard: skip if the character immediately before or after is a comma
                    // (already partially formatted) or a dot (decimal number).
                    const charBefore = seg.text[m.index - 1];
                    const charAfter = seg.text[m.index + original.length];
                    if (charBefore === ',' || charBefore === '.') continue;
                    if (charAfter === ',' || charAfter === '.') continue;

                    // Build the comma-formatted suggestion
                    const suggestion = original.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

                    // No change needed (already formatted — shouldn't occur with 5+ digit guard)
                    if (suggestion === original) continue;

                    seg.emit(
                        original,
                        suggestion,
                        m.index,
                        this.description
                    );
                }
            }

            return staleIds;
        },
    },



    TitleFormatting: {
        name: "Title Formatting",
        description: "Lowercase title, convert math to \\mth, chemistry to \\chem, update shorttitle",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();

            // ── Extract content inside \cmd{...} with brace matching ──
            function extractBraceContent(text, startIndex) {
                let depth = 1, i = startIndex, content = '';
                while (i < text.length && depth > 0) {
                    if (text[i] === '{') depth++;
                    else if (text[i] === '}') { depth--; if (depth === 0) break; }
                    content += text[i]; i++;
                }
                return { content, end: i };
            }

            // ── Tokenize content into: text, math ($...$), cmd (\cmd{...}) ──
            function tokenize(content) {
                const tokens = [];
                let pos = 0;

                while (pos < content.length) {
                    // $$ display math
                    if (content[pos] === '$' && content[pos + 1] === '$') {
                        const end = content.indexOf('$$', pos + 2);
                        if (end !== -1) {
                            tokens.push({ type: 'math', value: content.slice(pos, end + 2) });
                            pos = end + 2; continue;
                        }
                    }
                    // $ inline math
                    if (content[pos] === '$') {
                        const end = content.indexOf('$', pos + 1);
                        if (end !== -1) {
                            tokens.push({ type: 'math', value: content.slice(pos, end + 1) });
                            pos = end + 1; continue;
                        }
                    }
                    // \cmd{...} or bare \cmd
                    if (content[pos] === '\\') {
                        let j = pos + 1;
                        while (j < content.length && /[a-zA-Z*]/.test(content[j])) j++;
                        const cmd = content.slice(pos + 1, j);
                        if (j < content.length && content[j] === '{') {
                            let d = 1, k = j + 1, inner = '';
                            while (k < content.length && d > 0) {
                                if (content[k] === '{') d++;
                                else if (content[k] === '}') { d--; if (d === 0) break; }
                                inner += content[k]; k++;
                            }
                            tokens.push({ type: 'cmd', cmd, inner, raw: content.slice(pos, k + 1) });
                            pos = k + 1; continue;
                        }
                        tokens.push({ type: 'bare', value: content.slice(pos, j) });
                        pos = j; continue;
                    }
                    // plain text
                    let j = pos;
                    while (j < content.length && content[j] !== '$' && content[j] !== '\\') j++;
                    if (j > pos) tokens.push({ type: 'text', value: content.slice(pos, j) });
                    pos = j;
                }
                return tokens;
            }

            // ── Lowercase text preserving first letter of entire title ──
            function lowercaseTokens(tokens) {
                let firstLetterDone = false;
                return tokens.map(tok => {
                    if (tok.type !== 'text') return tok;
                    let val = tok.value;
                    if (!firstLetterDone) {
                        const li = val.search(/[a-zA-Z]/);
                        if (li !== -1) {
                            val = val.slice(0, li) +
                                val[li].toUpperCase() +
                                val.slice(li + 1).toLowerCase();
                            firstLetterDone = true;
                        } else {
                            val = val.toLowerCase();
                        }
                    } else {
                        val = val.toLowerCase();
                    }
                    return { ...tok, value: val };
                });
            }

            // ── Detect chemistry: text token ending with element + next is subscript math ──
            // e.g. text="H" next=$_2$ next-next=text starting with "O"
            function isChemSubscript(mathVal) {
                return /^\$_\{?\d+\}?\$$|^\$\^\{?\d+\}?\$$/.test(mathVal);
            }

            // ── Rebuild for \title — convert math to \mth, chemistry to \chem ──
            function buildTitleContent(tokens) {
                let result = '';
                let i = 0;
                while (i < tokens.length) {
                    const tok = tokens[i];

                    if (tok.type === 'text') {
                        // Check if this ends with a chemistry element before $_2$ style math
                        const chemMatch = tok.value.match(/^(.*?)([A-Z][a-z]?)$/s);
                        const nextTok = tokens[i + 1];
                        const afterNext = tokens[i + 2];

                        if (chemMatch && nextTok?.type === 'math' && isChemSubscript(nextTok.value)) {
                            // Extract subscript number
                            const sub = nextTok.value.replace(/^\$_?\{?/, '').replace(/\}?\$$/, '');
                            const elem = chemMatch[2];
                            // Collect trailing element from text after subscript
                            let trail = '';
                            if (afterNext?.type === 'text') {
                                const trailMatch = afterNext.value.match(/^([A-Z][a-z]?)/);
                                if (trailMatch) {
                                    trail = trailMatch[1];
                                    tokens[i + 2] = {
                                        ...afterNext,
                                        value: afterNext.value.slice(trailMatch[0].length)
                                    };
                                }
                            }
                            result += chemMatch[1] + `\\chem{${elem}_${sub}${trail}}`;
                            i += 2; // skip text + math tokens
                            continue;
                        }
                        result += tok.value;

                    } else if (tok.type === 'math') {
                        // Regular math — convert $x$ → \mth{x}
                        const inner = tok.value.replace(/^\$/, '').replace(/\$$/, '');
                        result += `\\mth{${inner}}`;

                    } else if (tok.type === 'cmd') {
                        result += tok.raw;
                    } else if (tok.type === 'bare') {
                        result += tok.value;
                    }
                    i++;
                }
                return result;
            }

            // ── Rebuild for \shorttitle — keep math/chemistry as-is, only lowercase text ──
            function buildShortTitleContent(tokens) {
                return tokens.map(tok => {
                    if (tok.type === 'math') return tok.value;        // keep $...$ unchanged
                    if (tok.type === 'cmd') return tok.raw;          // keep \cmd{} unchanged
                    if (tok.type === 'bare') return tok.value;        // keep \cmd unchanged
                    return tok.value;                                  // lowercased text
                }).join('');
            }

            // ── Find \title{...} ──
            const titleMatch = /\\title\s*\{/.exec(texFile);
            if (!titleMatch) return staleIds;

            const titleContentStart = titleMatch.index + titleMatch[0].length;
            const { content: titleContent, end: titleEnd } =
                extractBraceContent(texFile, titleContentStart);

            const titleStart = titleMatch.index;
            const originalTitle = texFile.slice(titleStart, titleEnd + 1);

            // Tokenize original title content
            const rawTokens = tokenize(titleContent);
            const lowercasedTokens = lowercaseTokens(rawTokens);

            const newTitleContent = buildTitleContent([...lowercasedTokens]);
            const newShortTitleContent = buildShortTitleContent([...lowercasedTokens]);

            const newTitleTag = `\\title{${newTitleContent}}`;

            // ── Find \shorttitle after \title ──
            const afterTitle = texFile.slice(titleEnd + 1);
            const shortTitleMatch = /^(\s*\\shorttitle\s*\{)/.exec(afterTitle);

            let fullOriginal, fullSuggestion;

            if (shortTitleMatch) {
                // \shorttitle exists — replace its content
                const shortStart = titleEnd + 1 +
                    afterTitle.indexOf(shortTitleMatch[0]);
                const shortContentStart = shortStart + shortTitleMatch[0].length;
                const { content: shortContent, end: shortEnd } =
                    extractBraceContent(texFile, shortContentStart);

                const originalShortTag = texFile.slice(shortStart, shortEnd + 1);
                const newShortTag = `\\shorttitle{${newShortTitleContent}}`;

                fullOriginal = originalTitle + texFile.slice(titleEnd + 1, shortEnd + 1);
                fullSuggestion = `${newTitleTag}\n${newShortTag}`;
            } else {
                // No \shorttitle — insert after \title
                fullOriginal = originalTitle;
                fullSuggestion = `${newTitleTag}\n\\shorttitle{${newShortTitleContent}}`;
            }

            // Skip if already correct
            if (fullOriginal.trim() === fullSuggestion.trim()) return staleIds;

            const position = getPositionFromIndex(texFile, titleStart, fullOriginal.length);

            if (isRejectedByLivePosition(position, this.name, fullOriginal)) return staleIds;

            const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
            if (overlapping.length > 0) {
                overlapping.forEach(old => staleIds.add(old.id));
            } else {
                if (isAlreadyAnchored(position, this.name, existingSuggestions)) return staleIds;
            }

            createAnchor(session, Range, position,
                fullOriginal, fullSuggestion,
                this.markerClass, this.name, this.description, anchorlist);

            return staleIds;
        }
    },

    //passed here
    TimeFormate: {
        name: "Time Format",
        description: "Convert AM/PM to a.m./p.m.",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TimeFomattingBefore.png",
        afterImage: "/images/description/TimeFomattingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(?<![\\])\b(AM|PM|am|pm)\b/gm;
            const map = { am: "a.m.", pm: "p.m.", AM: "A.M.", PM: "P.M." };
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = map[fullMatch];
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    PercentageSymbol: {
        name: "Change Percentage to %",
        description: "Change Percentage words to %",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/PercentageBefore.png",
        afterImage: "/images/description/PercentageAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\b(\d+)\s*(percent|percentage)\b/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\b(\d+)\s*(percent|percentage)\b/gi, "$1%");
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    //no before and after
    // HeadingTitleCase: {
    //     name: "Heading Title Case",
    //     description: "All headings should be in Title Case, capitalize after periods, preserve math and LaTeX commands",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const headingPattern =
    //             /\\(section|subsection|subsubsection|chapter|paragraph|subparagraph)\s*\*?\s*\{/g;
    //         let match;

    //         while ((match = headingPattern.exec(texFile)) !== null) {
    //             const commandType = match[1];
    //             const hasAsterisk = match[0].includes('*');
    //             const captionStartIndex = match.index + match[0].length - 1;
    //             const capturedBracedData = texFile.slice(captionStartIndex);
    //             const capturedText = getInsideTextofBraces(capturedBracedData);
    //             if (!capturedText) continue;

    //             const innerContent = capturedText.slice(1, -1);
    //             const finalContent = capitalizeInsideBraces(innerContent, true);
    //             if (finalContent === innerContent) continue;

    //             const commandPrefix = hasAsterisk ? `\\${commandType}*` : `\\${commandType}`;
    //             const fullMatch = `${commandPrefix}${capturedText}`;
    //             const suggestion = `${commandPrefix}{${finalContent}}`;
    //             const position = getPositionFromIndex(texFile, match.index, match[0].length - 1 +
    //                 capturedText.length);

    //             if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //             }

    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(session, Range, position, fullMatch, suggestion,
    //                     this.markerClass, this.name, this.description, anchorlist);
    //             }
    //         }

    //         return staleIds;
    //     }
    // },  

    //No Before and after
    // FigureCaptions: {
    //     name: "Figure Caption Sentence Case",
    //     description: "Figure captions should be sentence case, capitalize after periods, preserve math and LaTeX commands, end with period",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const figurePattern = /\\begin\{figure\*?\}[\s\S]*?\\caption\s*\{/g;
    //         let match;

    //         while ((match = figurePattern.exec(texFile)) !== null) {
    //             const captionStartIndex = match.index + match[0].length - 1;
    //             const capturedBracedData = texFile.slice(captionStartIndex);
    //             const capturedText = getInsideTextofBraces(capturedBracedData);
    //             if (!capturedText) continue;

    //             const innerContent = capturedText.slice(1, -1);
    //             let finalContent = capitalizeInsideBraces(innerContent, true);
    //             const trimmed = finalContent.trim();
    //             if (!trimmed.endsWith('.')) finalContent = trimmed + '.';
    //             if (finalContent === innerContent) continue;

    //             const captionMatch = texFile.substring(match.index).match(/\\caption\s*\{/);
    //             const actualCaptionIndex = match.index + captionMatch.index;
    //             const fullMatch = `\\caption${capturedText}`;
    //             const suggestion = `\\caption{${finalContent}}`;
    //             const position = getPositionFromIndex(texFile, actualCaptionIndex, fullMatch.length);

    //             if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //             }

    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(session, Range, position, fullMatch, suggestion,
    //                     this.markerClass, this.name, this.description, anchorlist);
    //             }
    //         }

    //         return staleIds;
    //     }
    // },

    // FigureCaptions: {
    //     name: "Figure Caption Sentence Case",
    //     description: "Figure captions should be sentence case, capitalize after periods, preserve math and LaTeX commands, end with period",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
    //         const staleIds = new Set();

    //         const emit = makeAnchorEmitter({
    //             texFile, session, Range, anchorlist, existingSuggestions, staleIds, styleName: this.name, markerClass: this.markerClass, overlapMode: 'simple',
    //         });

    //         // Match the start of figure environments and find the \caption command
    //         const figurePattern = /\\begin\{figure\*?\}[\s\S]*?\\caption\s*\{/g;
    //         let match;

    //         while ((match = figurePattern.exec(texFile)) !== null) {
    //             if (isCommented(texFile, match.index)) continue;

    //             const captionStartIndex = match.index + match[0].length - 1;
    //             const capturedBracedData = texFile.slice(captionStartIndex);
    //             const capturedText = getInsideTextofBraces(capturedBracedData); // returns "{content}"

    //             if (!capturedText) continue;

    //             const innerContent = capturedText.slice(1, -1); // remove the { }

    //             // let finalContent = capitalizeInsideBraces(innerContent, false);
    //             let finalContent = changeToSentenceCase(innerContent);

    //             // 3. Ensure it ends with a period
    //             const trimmed = finalContent.trim();
    //             if (trimmed.length > 0 && !trimmed.endsWith('.')) {
    //                 finalContent = trimmed + '.';
    //             }

    //             // 4. Construct the full strings for the emitter
    //             const fullMatch = `\\caption${capturedText}`;
    //             const suggestion = `\\caption{${finalContent}}`;

    //             if (fullMatch === suggestion) continue;

    //             // 5. Find the actual start of the \caption command for accurate anchoring
    //             const captionCommandOffset = match[0].lastIndexOf('\\caption');
    //             const absIndex = match.index + captionCommandOffset;

    //             emit(
    //                 fullMatch,          // originalText
    //                 suggestion,         // suggestionText
    //                 absIndex,           // absolute Index in the file
    //                 this.description    // message shown to user
    //             );
    //         }

    //         return staleIds;
    //     }
    // },
    FigureCaptions: {
        name: "Figure Caption Sentence Case",
        description: "Standardize figure captions to sentence case, preserving names, math, and ending with a period.",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();
            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name, markerClass: this.markerClass, overlapMode: 'simple',
            });

            // 1. Regex to find the entire figure environment block
            // This matches \begin{figure} ... \end{figure} including starred versions
            const figureBlockPat = /\\begin\{(figure\*?)\}([\s\S]*?)\\end\{\1\}/g;
            let figMatch;

            while ((figMatch = figureBlockPat.exec(texFile)) !== null) {
                if (isCommented(texFile, figMatch.index)) continue;

                const figureBody = figMatch[2]; // The content inside \begin and \end
                const figureStartIndex = figMatch.index;

                // 2. Search for \caption only inside this specific figure body
                const captionPat = /\\caption\s*\{/g;
                let capMatch;

                while ((capMatch = captionPat.exec(figureBody)) !== null) {
                    // Determine the absolute index of the caption in the whole file
                    const captionAbsStart = figureStartIndex + figMatch[0].indexOf(capMatch[0], figMatch[0].indexOf(figureBody));

                    // 3. Brace-balanced extraction for the content
                    let depth = 1, i = capMatch.index + capMatch[0].length, content = '';
                    while (i < figureBody.length && depth > 0) {
                        if (figureBody[i] === '{') depth++;
                        else if (figureBody[i] === '}') {
                            depth--;
                            if (depth === 0) break;
                        }
                        content += figureBody[i];
                        i++;
                    }

                    const fullMatch = `\\caption{${content}}`;

                    // 4. Transform the content
                    let newContent = changeToSentenceCase(content);

                    // Ensure it ends with a period
                    // const trimmed = newContent.trim();
                    // if (trimmed.length > 0 && !trimmed.endsWith('.')) {
                    //     newContent = newContent.replace(/(\S)(\s*)$/, '$1.$2');
                    // }

                    const suggestion = `\\caption{${newContent}}`;

                    // 5. Emit if a change is needed
                    if (fullMatch !== suggestion) {
                        emit(
                            fullMatch,      // originalText
                            suggestion,     // suggestionText
                            captionAbsStart, // absolute index in the texFile
                            this.description
                        );
                    }
                }
            }
            return staleIds;
        }
    },



    //No before and after
    TableCaptions: {
        name: "Table Caption Sentence Case",
        description: "Table captions should be sentence case, capitalize after periods, preserve math and LaTeX commands, end with period",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const tablePattern = /\\begin\{table\*?\}[\s\S]*?\\caption\s*\{/g;
            let match;

            while ((match = tablePattern.exec(texFile)) !== null) {
                const captionStartIndex = match.index + match[0].length - 1;
                const capturedBracedData = texFile.slice(captionStartIndex);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                let finalContent = capitalizeInsideBraces(innerContent, true);
                const trimmed = finalContent.trim();
                if (!trimmed.endsWith('.')) finalContent = trimmed + '.';
                if (finalContent === innerContent) continue;

                const captionMatch = texFile.substring(match.index).match(/\\caption\s*\{/);
                const actualCaptionIndex = match.index + captionMatch.index;
                const fullMatch = `\\caption${capturedText}`;
                const suggestion = `\\caption{${finalContent}}`;
                const position = getPositionFromIndex(texFile, actualCaptionIndex, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    EmDashSpacing: {
        name: "Em-dash Spacing",
        description: "No space before and after em-dash",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/EmdashingBefore.png",
        afterImage: "/images/description/EmdashingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\s+---\s+/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = '---';
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    SlashRemoveSpacing: {
        name: "Slash Remove Spacing",
        description: "No space before and after a slash",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/slashRemoveSpacingBefore.png",
        afterImage: "/images/description/slashRemoveSpacingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\w+)\s+\/\s+(\w+)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}/${match[2]}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    VersusUsage: {
        name: "Versus Usage",
        description: "Spell out 'versus' outside parenthesis, abbreviate 'vs.' inside",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/versusBefore.png",
        afterImage: "/images/description/versusAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const tryAnchor = (fullMatch, suggestion, position) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            };

            // "versus" inside parenthesis → "vs." 
            const pattern1 = /\([^)]*\bversus\b[^)]*\)/gi;
            let match;
            while ((match = pattern1.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\bversus\b/gi, 'vs.');
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);
                tryAnchor(fullMatch, suggestion, position);
            }

            // "vs." outside parenthesis → "versus" 
            const pattern2 = /(?<!\()\b\w+\s+vs\.\s+\w+\b(?!\))/gi;
            while ((match = pattern2.exec(texFile)) !== null) {
                const before = texFile.substring(Math.max(0, match.index - 50), match.index);
                const openBefore = (before.match(/\(/g) || []).length;
                const closeBefore = (before.match(/\)/g) || []).length;
                if (openBefore !== closeBefore) continue;

                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\bvs\./gi, 'versus');
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);
                tryAnchor(fullMatch, suggestion, position);
            }

            return staleIds;
        }
    },
    RemoveDuplicateReferences: {
        name: "Remove Duplicate References",
        description: "Remove duplicate bibliography entries, keeping only the first occurrence",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RemoveDuplicateReferenceBefore.png",
        afterImage: "/images/description/RemoveDuplicateReferenceAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                overlapMode: 'simple',
            });

            const bibitemRegex =
                /\\bibitem\{(.*?)\}%?\d*\s*([\s\S]*?)(?=\\bibitem|\\end\{thebibliography\}|\\AQ)/g;

            // seen: normalizedContent → { key: string }
            // Tracks the FIRST occurrence of each unique reference content
            // so we can name both the original and the duplicate in the query.
            const seen = new Map();

            let match;
            while ((match = bibitemRegex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const bibKey = match[1].trim();   // e.g. "ref51"
                const content = match[2];

                // Normalize content for comparison (ignore whitespace, braces, case)
                const normalized = content
                    .replace(/\s+/g, ' ')
                    .replace(/[{}]/g, '')
                    .toLowerCase()
                    .trim();

                if (isCommented(texFile, match.index)) continue;

                if (seen.has(normalized)) {
                    // ── Duplicate found ───────────────────────────────────────────
                    const { key: firstKey } = seen.get(normalized);

                    // Build the per-match query that names both keys explicitly:
                    //   "\\bibitem{ref51} is a duplicate of \\bibitem{ref50}..."
                    const queryDescription =
                        `References \\bibitem{${bibKey}} and \\bibitem{${firstKey}} appear to be duplicates. We have removed the ` +
                        ` [${bibKey}] in the list as well as updated the corresponding text citations. Kindly review and confirm. `
                    // suggestionText = '' means "delete this entire block"
                    emit(fullMatch, `\\AQ{${queryDescription}}\n`, match.index, queryDescription);

                } else {
                    // First time we see this content — record key for later duplicates
                    seen.set(normalized, { key: bibKey });
                }
            }

            return staleIds;
        }
    },

    TemperatureFormatting: {
        name: "Temperature Formatting",
        description: "Remove space before and after °",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TemperatureFormatingBefore.png",
        afterImage: "/images/description/TemperatureFormatingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\d+)\s*°\s*([A-Z]{1,2})/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}°${match[2]}`;
                if (fullMatch === suggestion) continue;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    SectionCallout: {
        name: "Section Callout Capitalization",
        description: "Capitalize 'Section' in section callouts",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/SectionCalloutBefore.png",
        afterImage: "/images/description/SectionCalloutAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\bsection\s*(~)?\\ref\{/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (!match[0].startsWith('section')) continue;
                const fullMatch = match[0];
                const suggestion = `Section~\\ref{`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    RepeatedWords: {
        name: "Delete Repeated Words",
        description: "Remove duplicate consecutive words",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RepeatedWordsBefore.png",
        afterImage: "/images/description/RepeatedWordsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const pattern = /\b(\w+)\s+\1\b/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]} \\AQ{Repeated word deleted}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) {
                    // ── Still advance by one word so next repeat is caught ──
                    pattern.lastIndex = match.index + match[1].length + 1;
                    continue;
                }

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                        // ── Advance by one word for overlap detection too ──
                        pattern.lastIndex = match.index + match[1].length + 1;
                        continue;
                    }
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }


                pattern.lastIndex = match.index + match[1].length + 1;
            }

            return staleIds;
        }
    },



    LatinTermsItalic: {
        name: "Latin Terms Italic",
        description: "Latin terms should be italicized",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/Latexwordsbefore.png",
        afterImage: "/images/description/LatexwordsAfter.png",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();

            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                overlapMode: 'simple'
            });

            const latinTerms = [
                'in vitro', 'in vivo', 'via', 'in situ', 'et al', 'etc', 'i.e.', 'e.g.',
                'cf.', 'vs.', 'vice versa', 'ad hoc', 'a priori', 'a posteriori',
                'de facto', 'de jure', 'per se', 'inter alia', 'ex post', 'ex ante'
            ];

            for (const term of latinTerms) {
                const escapedTerm = term.replace(/\./g, '\\.');
                // Prevents matching if already inside \textit{ and prevents matching partial words
                const pattern = new RegExp(`(?<!\\\\textit\\{)${escapedTerm}(?![\\w}])`, 'gi');
                let match;

                while ((match = pattern.exec(texFile)) !== null) {
                    // Skip if inside a comment (using the same check from your US/UK method)
                    if (typeof isCommented === 'function' && isCommented(texFile, match.index)) continue;

                    const originalText = match[0];
                    const suggestedText = `\\textit{${originalText}}`;

                    // The emitter automatically handles positions, overlaps, and stale IDs
                    emit(
                        originalText,
                        suggestedText,
                        match.index,
                        this.description
                    );
                }
            }

            return staleIds;
        }
    },

    EquationCitation: {
        name: "Equation Citation",
        description: "Use \\eqref{} instead of (\\ref{})",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/EquationCitationBefore.png",
        afterImage: "/images/description/EquationCitationAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const tryAnchor = (fullMatch, suggestion, position) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            };

            const pattern1 = /Equation(~)?\s*\\ref\{([^}]+)\}/gi;
            let match;
            while ((match = pattern1.exec(texFile)) !== null) {
                const position = getPositionFromIndex(texFile, match.index, match[0].length);
                tryAnchor(match[0], `Equation~\\eqref{${match[2]}}`, position);
            }

            const pattern2 = /\(\\ref\{([^}]+)\}\)/g;
            while ((match = pattern2.exec(texFile)) !== null) {
                const position = getPositionFromIndex(texFile, match.index, match[0].length);
                tryAnchor(match[0], `\\eqref{${match[1]}}`, position);
            }

            return staleIds;
        }
    },


    LatexLabels: {
        name: "LaTeX Command Spacing",
        description: "Ensure proper spacing after LaTeX commands after \\cite{} or \\ref{} or \\label{} add a space",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/LatexCommandSpacingBefore.png",
        afterImage: "/images/description/LatexCommandSpacingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function isInsideParens(text, index) {
                const before = text.lastIndexOf('(', index);
                if (before === -1) return false;
                const after = text.indexOf(')', index);
                if (after === -1) return false;
                const closingBefore = text.indexOf(')', before);
                return closingBefore > index;
            }

            const pattern = /(Figure|Table)(~| )?\\ref\{[^}]+\}([a-zA-Z])(?![\w])/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (!isInsideParens(texFile, match.index)) continue;

                const fullMatch = match[0];
                const refMatch = fullMatch.match(/\\ref\{[^}]+\}/);
                if (!refMatch) continue;

                const abbrev = match[1] === "Figure" ? "Fig." : "Tab.";
                const suggestion = `${abbrev} ${refMatch[0]}(${match[3]})`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    RepeatingRanges: {
        name: "Format Ranges",
        description: "Format number ranges properly",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/NumberRangesBefore.png",
        afterImage: "/images/description/NumberRangesAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const rules = [
                {
                    pattern: /\((\d+)%?\s*[—-]\s*(\d+)\\?%\)/g, suggest: (m) =>
                        `(${m[1]}\\%--${m[2]}\\%)`
                },
                {
                    pattern: /\bfrom\s+(\d+)\s*\\?%?\s*[—-]\s*(\d+)\s*\\?%/gi, suggest: (m) => `from 
    ${m[1]}\\% to ${m[2]}\\%`
                },
                {
                    pattern: /\bbetween\s+(\d+)\s*\\?%?\s*[—-]\s*(\d+)\s*\\?%/gi, suggest: (m) => `between 
    ${m[1]}\\% and ${m[2]}\\%`
                },
            ];

            for (const { pattern, suggest } of rules) {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const suggestion = suggest(match);
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, suggestion,
                            this.markerClass, this.name, this.description, anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },

    TimeAndDosageFormatting: {
        name: "Normalize Time/Dosage Ratios",
        description: "Convert unit ratios like second/day to sec/d",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TimeandDosageBefore.png",
        afterImage: "/images/description/TimeandDosageAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const unitMap = {
                second: "sec", seconds: "sec", sec: "sec",
                minute: "min", minutes: "min", min: "min",
                hour: "h", hours: "h", h: "h",
                day: "d", days: "d", d: "d",
                milligram: "mg", milligrams: "mg", mg: "mg",
                gram: "g", grams: "g", g: "g"
            };
            const keys = Object.keys(unitMap).join("|");
            const pattern = new RegExp(`\\b(${keys})\\s*\\/\\s*(${keys})\\b`, "gi");
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion =
                    `${unitMap[match[1].toLowerCase()]}/${unitMap[match[2].toLowerCase()]}`;
                if (fullMatch === suggestion) continue;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    LatexCitations: {
        name: "LaTeX Citations",
        description: "Convert Equation, Table, and Figure references to LaTeX \\ref{} format",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/LatexCitationsBefore.png",
        afterImage: "/images/description/LatexCitationsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const rules = [
                {
                    pattern: /\b(Equation|Eq\.)\s*\((\d+)\)/gi, suggest: (m) => `${m[1]} 
    (\\ref{eqn${m[2]}})`
                },
                {
                    pattern: /\b(Table)\s+(\d+)\b/gi, suggest: (m) => `${m[1]} 
    \\ref{table-${m[2]}}`
                },
                {
                    pattern: /\b(Tables)\s+(\d+)\s+to\s+(\d+)\b/gi, suggest: (m) => `${m[1]} 
    \\ref{table-${m[2]}} to \\ref{table-${m[3]}}`
                },
                {
                    pattern: /\b(Figure|Fig\.)\s+(\d+)\b/gi, suggest: (m) => `${m[1]} \\ref{fig-${m[2]}}`
                },
            ];

            for (const { pattern, suggest } of rules) {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const suggestion = suggest(match);
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, suggestion,
                            this.markerClass, this.name, this.description, anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },




    // URLValidations: {
    //     name: "Invalid URL or DOIs",
    //     description: "Validates URLs and DOIs. Flags links that return a 404 or 410.",
    //     markerClass: "ace-Common-highlight",

    //     async execute(texFile, session, Range, anchorList, fileName, Allfiles,
    //         existingSuggestions = [], rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const self = this;

    //         existingSuggestions.forEach(s => {
    //             if (s.styleInfo?.name === self.name) staleIds.add(s.id);
    //         });

    //         const { segments } = buildStyleContext({
    //             texFile, session, Range, anchorList,
    //             existingSuggestions, staleIds,
    //             styleName: this.name,
    //             markerClass: this.markerClass,
    //             scope: { wholeDocument: false },
    //             excludeMath: true,
    //             mode: "query",
    //             overlapMode: "refresh",
    //         });


    //         const validateLink = async (url) => {
    //             const controller = new AbortController();
    //             const timer = setTimeout(() => controller.abort(), 6000);
    //             try {
    //                 console.log("urlssss", url)
    //                 const res = await fetch(url, {
    //                     method: "GET",
    //                     redirect: "follow",
    //                     cache: "no-cache",
    //                     signal: controller.signal,
    //                 });
    //                 clearTimeout(timer);
    //                 if (res.status === 404) return { valid: false, reason: "404 Not Found" };
    //                 if (res.status === 410) return { valid: false, reason: "410 Gone" };
    //                 return { valid: true };
    //             } catch (err) {
    //                 clearTimeout(timer);
    //                 return { valid: true };
    //             }
    //         };

    //         const patterns = {
    //             bracedUrl: /\\(?:href|url|Refdoi|orcidlogo|email)\s*\{([^{}]{4,300})\}(?:\{([^{}]*)\})?/gi,
    //             doi: /\bDOI:\s*(10\.\d{4,9}\/[^\s\),\}]{3,})/gi,
    //         };
    //         const candidates = [];

    //         for (const seg of segments) {
    //             patterns.bracedUrl.lastIndex = 0;
    //             let m;
    //             while ((m = patterns.bracedUrl.exec(seg.text)) !== null) {
    //                 const args = [m[1], m[2]].filter(a => a?.trim().match(/^https?:\/\//i));
    //                 for (const raw of args) {
    //                     candidates.push({
    //                         seg,
    //                         matchText: m[0],
    //                         url: raw.trim(),
    //                         label: `Link "${raw.trim()}"`,
    //                         isDoi: false,
    //                         index: m.index,
    //                     });
    //                 }
    //             }

    //             // DOIs
    //             patterns.doi.lastIndex = 0;
    //             while ((m = patterns.doi.exec(seg.text)) !== null) {
    //                 const doi = m[1].trim().replace(/[.,;]+$/, "");
    //                 candidates.push({
    //                     seg,
    //                     matchText: m[0],
    //                     url: `https://doi.org/${doi}`,
    //                     label: `DOI "${doi}"`,
    //                     isDoi: true,
    //                     index: m.index,
    //                 });
    //             }
    //         }
    //         const seen = new Set();
    //         const unique = candidates.filter(c => {
    //             if (seen.has(c.url)) return false;
    //             seen.add(c.url);
    //             return true;
    //         });

    //         const results = await Promise.all(
    //             unique.map(async c => ({ ...c, result: await validateLink(c.url) }))
    //         );

    //         // results
    //         //     .filter(r => !r.result.valid)
    //         //     .forEach(r => {
    //         //         const title = r.isDoi ? "The provided URL is not accessible. Kindly supply a valid and functional link." : "The provided URL is not accessible. Kindly supply a valid and functional link.";
    //         //         const message = `${r.label} appears broken (${r.result.reason}).`;
    //         //         r.seg.emitQuery(r.matchText, message, r.index, title);
    //         //     });
    //         results
    //             .filter(r => !r.result.valid)
    //             .forEach(r => {

    //                 const title =
    //                     "The provided URL is not accessible. Kindly supply a valid and functional link.";

    //                 const message =
    //                     `${r.label} returned ${r.result.reason}. Please verify and update the reference.`;

    //                 r.seg.emitQuery(
    //                     r.matchText,
    //                     message,
    //                     r.index,
    //                     title
    //                 );
    //             });

    //         return staleIds;
    //     },
    // },

    URLValidations: {
        name: "Invalid URL or DOIs",
        description: "Validates URLs and DOIs. Flags links that return a 404 or 410.",
        markerClass: "ace-Common-highlight",

        async execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();
            const self = this;

            existingSuggestions.forEach(s => {
                if (s.styleInfo?.name === self.name) staleIds.add(s.id);
            });

            const ctx = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                // scope: { wholeDocument: true },
                scope: {},
                excludeMath: false,
                mode: "query",
                overlapMode: "refresh",
            });


            // const validateLink = async (url) => {


            //     try {
            //         const ctrl = new AbortController();
            //         const t = setTimeout(() => ctrl.abort(), 800000);
            //         const res = await fetch(
            //             `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
            //             { signal: ctrl.signal }
            //         );
            //         console.log("urls", url, "status", res.status, "statustext", res.statusText);
            //         clearTimeout(t);
            //         if (res.ok) {
            //             const data = await res.json();
            //             console.log("dataaa", data);
            //             const code = data?.status?.http_code;
            //             console.log(`[allorigins] ${url} → ${code}`);
            //             if (code) {
            //                 if (code === 200) return { valid: true };
            //                 return { valid: false, reason: `${code} Error` };
            //             }
            //         }
            //     } catch (e) {
            //         console.log(`allorigins failed for ${url}: ${e.message}`);
            //     }


            //     try {
            //         const ctrl = new AbortController();
            //         const t = setTimeout(() => ctrl.abort(), 8000);
            //         const res = await fetch(
            //             `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
            //             { method: "GET", signal: ctrl.signal }
            //         );
            //         clearTimeout(t);
            //         console.log(`corsproxy ${url} → ${res.status}`);
            //         if (res.status === 200) return { valid: true };
            //         return { valid: false, reason: `${res.status} Error` };
            //     } catch (e) {
            //         console.log(`corsproxy failed for ${url}: ${e.message}`);
            //     }


            //     try {
            //         const ctrl = new AbortController();
            //         const t = setTimeout(() => ctrl.abort(), 8000);
            //         const res = await fetch(
            //             `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
            //             { method: "GET", signal: ctrl.signal }
            //         );
            //         clearTimeout(t);
            //         console.log(`codetabs ${url} → ${res.status}`);
            //         if (res.status === 200) return { valid: true };
            //         return { valid: false, reason: `${res.status} Error` };
            //     } catch (e) {
            //         console.log(`codetabs failed for ${url}: ${e.message}`);
            //     }


            //     console.log(`All proxies failed for ${url} — skipping`);
            //     return { valid: true };
            // };



            const validateLink = async (url) => {


                try {
                    const parsed = new URL(url);
                    if (/\s/.test(parsed.hostname) || /\s/.test(parsed.pathname)) {
                        return { valid: false, reason: "Invalid URL (contains spaces)" };
                    }
                } catch (e) {
                    return { valid: false, reason: "Invalid URL format" };
                }


                const TRUSTED_DOMAINS = [
                    'doi.org', 'orcid.org', 'creativecommons.org', 'arxiv.org',
                    'springer.com', 'elsevier.com', 'wiley.com', 'nature.com',
                    'ieee.org', 'acm.org', 'siam.org', 'ams.org', 'jstor.org',
                    'github.com', 'wikipedia.org',
                ];

                try {
                    const hostname = new URL(url).hostname.toLowerCase();
                    const isTrusted = TRUSTED_DOMAINS.some(d =>
                        hostname === d || hostname.endsWith(`.${d}`)
                    );
                    if (isTrusted) {
                        console.log(`trusted ${url} — skipping validation`);
                        return { valid: true };
                    }
                } catch (e) { }

                try {
                    const ctrl = new AbortController();
                    const t = setTimeout(() => ctrl.abort(), 10000);
                    const res = await fetch(
                        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
                        { signal: ctrl.signal }
                    );
                    clearTimeout(t);


                    let code = null;
                    try {
                        const data = await res.json();
                        code = data?.status?.http_code;
                    } catch (parseErr) {
                        console.log(`parse failed: ${parseErr.message}`);
                    }

                    console.log(`allorigins ${url} → http_code: ${code}, res.status: ${res.status}`);

                    if (code === 200) return { valid: true };
                    if (code === 404) return { valid: false, reason: "404 Not Found" };
                    if (code === 410) return { valid: false, reason: "410 Gone" };
                    if (code === 400) return { valid: false, reason: "400 Bad Request" };
                    if (code >= 500) {
                        console.log(`${code} — skipping`);
                        return { valid: true };
                    }


                    if (res.status === 400) return { valid: false, reason: "400 Bad Request" };
                    if (res.status === 404) return { valid: false, reason: "404 Not Found" };
                    if (res.status >= 500) return { valid: true };

                } catch (e) {
                    console.log(`allorigins failed: ${e.message}`);
                }


                console.log(`Cannot verify ${url} — skipping`);
                return { valid: true };
            };







            // texFile direct scan
            const candidates = [];
            const bracedUrlPat = /\\(?:href|url|Refdoi|orcidlogo|email)\s*\{([^{}]{4,300})\}(?:\{([^{}]*)\})?/gi;
            const doiPat = /\bDOI:\s*(10\.\d{4,9}\/[^\s\),\}]{3,})/gi;
            let m;

            bracedUrlPat.lastIndex = 0;
            while ((m = bracedUrlPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;
                const args = [m[1], m[2]].filter(a => a?.trim().match(/^https?:\/\//i));
                for (const raw of args) {
                    candidates.push({
                        matchText: m[0],
                        url: raw.trim(),
                        label: `Link "${raw.trim()}"`,
                        isDoi: false,
                        absIndex: m.index,
                    });
                }
            }

            doiPat.lastIndex = 0;
            while ((m = doiPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;
                const doi = m[1].trim().replace(/[.,;]+$/, "");
                candidates.push({
                    matchText: m[0],
                    url: `https://doi.org/${doi}`,
                    label: `DOI "${doi}"`,
                    isDoi: true,
                    absIndex: m.index,
                });
            }

            const seen = new Set();
            const unique = candidates.filter(c => {
                if (seen.has(c.url)) return false;
                seen.add(c.url);
                return true;
            });

            const results = await Promise.all(
                unique.map(async c => ({ ...c, result: await validateLink(c.url) }))
            );

            // results
            //     .filter(r => !r.result.valid)
            //     .forEach(r => {
            //         const title = "The provided URL is not accessible. Kindly supply a valid and functional link.";
            //         const message = `${r.label} appears broken (${r.result.reason}).`;
            //         ctx.emitQuery(r.matchText, message, r.absIndex, title);


            //     });

            // console.log("RESULTS", results);
            const broken = results.filter(r => !r.result.valid);
            console.log("Broken URLs count:", broken.length);

            broken.forEach(r => {
                const title = "The provided URL is not accessible. Kindly supply a valid and functional link.";
                const message = `${r.label} appears broken (${r.result.reason}).`;
                console.log("emitQuery:", r.matchText, "index:", r.absIndex);
                ctx.emitQuery(r.matchText, message, r.absIndex, title);
            });

            return staleIds;
        },
    },










    AddSpacing: {
        name: "Adding Space Around Symbols",
        description: "Add space around symbols only if numbers or single-letter strings are on both sides",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/AddSpacingAroundSymbolsBefore.png",
        afterImage: "/images/description/AddSpacingAroundSymbolsAfter.png",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();

            const { segments } = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,

                // ── Scope ─────────────────────────────────────────────────────────
                scope: { wholeDocument: true },

                // ── Math exclusion ─────────────────────────────────────────────────
                // Skips $…$  $$…$$  \(…\)  \[…\]  and all named math environments.
                excludeMath: true,

                // ── Environment exclusion ──────────────────────────────────────────
                // excludeOnlyEnvironments removes the ENTIRE environment block
                // including every inner command inside it.
                // Listing 'table' here means \tabular, \hline, \multicolumn,
                // \textbf — anything nested — is also excluded automatically.
                // No need to list inner commands separately.
                excludeOnlyEnvironments: [
                    'table', 'figure',          // entire table/figure incl. all inner cmds
                    'longtable', 'sidewaystable',
                    'thebibliography',
                    'verbatim', 'lstlisting', 'minted', 'align', 'equation', 'tabular'
                ],

                // ── Inner-command exclusion ────────────────────────────────────────
                // These commands hold identifiers / URLs — never quantities.
                excludeInner: [
                    'label', 'ref', 'eqref', 'cite', 'bibitem',
                    'url', 'href', 'hyperref', 'author', 'address'
                ],
            });

            // Pattern: [left][symbol][right]  — no spaces around symbol yet.
            // Symbols: + ± < > / * - =
            // Captures left text, the symbol, and right text separately.
            // const pattern = /(?<![{])([A-Za-z0-9]+)([\+±<>\/\*\-=])([A-Za-z0-9]+)(?![}])/g;
            // const pattern = /(?<![{\^\}])([A-Za-z0-9]+)([\+±<>\/\*\-=])([A-Za-z0-9]+)(?![}\^])/g;
            const pattern = / ([A-Za-z0-9]+)([\+±<>\/\*\-=])([A-Za-z0-9]+) /g;

            for (const seg of segments) {
                pattern.lastIndex = 0;
                let m;
                while ((m = pattern.exec(seg.text)) !== null) {
                    const [fullMatch, left, symbol, right] = m;

                    // Rule: left must be digits-only OR a single letter.
                    //       right must be digits-only OR a single letter.
                    const leftValid = /^\d+$/.test(left) || /^[A-Za-z]$/.test(left);
                    const rightValid = /^\d+$/.test(right) || /^[A-Za-z]$/.test(right);
                    if (!leftValid || !rightValid) continue;

                    const suggestion = ` ${left} ${symbol} ${right} `;

                    seg.emit(
                        fullMatch,
                        suggestion,
                        m.index,
                        this.description
                    );
                }
            }

            return staleIds;
        },
    },
    RemoveSpacing: {
        name: "Removing Space Around Symbols",
        description: "Remove extra spaces around symbols where numbers (any length) or single-letter strings are on both sides",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RemoveSpacingAroundSymbolsBefore.png",
        afterImage: "/images/description/RemoveSpacingAroundSymbolsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /([A-Za-z0-9]+)\s+([\+±<>\/\*\-\=])\s+([A-Za-z0-9]+)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const [fullMatch, left, symbol, right] = match;
                pattern.lastIndex = match.index + 1;

                const leftValid = /^\d+$/.test(left) || /^[A-Za-z]$/.test(left);
                const rightValid = /^\d+$/.test(right) || /^[A-Za-z]$/.test(right);
                if (!leftValid || !rightValid) continue;

                const suggestion = `${left}${symbol}${right}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    dashclassification: {
        name: "Normalize Dash Between Numbers",
        description: "Change dash, en-dash, em-dash with spaces to a single '-' between numbers",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/dashclassificationBefore.png",
        afterImage: "/images/description/dashclassificationAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\d+)\s+[-–—]\s+(\d+)/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}-${match[2]}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    ConvertNumber: {
        name: "Convert number to Text upto 10",
        description: "Convert numbers (1-10) to their word equivalents in running text.",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();

            const { segments } = buildStyleContext({
                texFile,
                session,
                Range,
                anchorlist,
                existingSuggestions,
                staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true },

                // 1. Skip ALL math ($$, \[\], equation, etc.)
                excludeMath: true,

                // 2. Skip specific text commands where numbers should stay as digits
                excludeInner: ['url', 'href', 'verb', 'cite', 'ref', 'label', 'section', 'subsection', 'TRSTBL', 'caption'],

                // 3. Skip Tables and Figures as requested
                excludeEnvironments: [
                    'table', 'table*', 'tabular', 'figure', 'figure*',
                    'verbatim', 'lstlisting', 'algorithm', 'thebibliography'
                ],
            });

            const numbersMap = {
                "1": "one", "2": "two", "3": "three", "4": "four", "5": "five",
                "6": "six", "7": "seven", "8": "eight", "9": "nine", "10": "ten"
            };

            // Regex explanation: Match digits 1-10 with word boundaries
            // This ensures we don't match the "1" in "15" or "page 1"
            // const numberPat = /\b(10|[1-9])\b/g;
            const numberPat = /(?<![\w{}.,()-])(10|[1-9])(?![\w{}.,-])/g;

            for (const seg of segments) {
                numberPat.lastIndex = 0;
                let m;
                while ((m = numberPat.exec(seg.text)) !== null) {
                    const originalDigit = m[1];
                    const suggestionWord = numbersMap[originalDigit];

                    if (suggestionWord) {
                        seg.emit(
                            originalDigit,
                            suggestionWord,
                            m.index,
                            this.description
                        );
                    }
                }
            }

            return staleIds;
        }
    },



    cdotsCorrection: {
        name: "Ellipsis to \\cdots",
        description: "Replace \\ldots, \\vdots, or ... with \\cdots when they appear between mathematical operators.",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();

            // ── excludeMath: false — ellipsis inside $...$ and equation envs is where
            //    this rule applies. Math zones are exactly what we need to scan.
            //    We keep excludeAllCommands: false so command names are visible
            //    for the operator lookbehind/lookahead to work correctly.
            const { segments } = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true },
                excludeMath: false,   // MUST be false — rule applies inside math
                excludeAllCommands: false,  // need command tokens for operator detection
                overlapMode: 'simple',
            });

            // ── Operator list ─────────────────────────────────────────────────────
            // These are the binary operators after which an ellipsis token should be
            // \cdots (centered dots), not \ldots (baseline dots) or \vdots (vertical).
            //
            // Regex alternation pieces:
            //   Plain ASCII: +  -  =  <  >
            //   LaTeX cmds:  \times  \cdot  \div  \pm  \mp
            //                \to  \rightarrow  \leftarrow  \Rightarrow  \Leftarrow
            //                \leftrightarrow  \Leftrightarrow
            //                \cup  \cap  \oplus  \otimes  \wedge  \vee  \circ
            //                \leq  \geq  \neq
            const OP_PIECES = [
                '\\+', '-', '=', '<', '>',
                '\\\\times', '\\\\cdot', '\\\\div',
                '\\\\pm', '\\\\mp',
                '\\\\to', '\\\\rightarrow', '\\\\leftarrow',
                '\\\\Rightarrow', '\\\\Leftarrow',
                '\\\\leftrightarrow', '\\\\Leftrightarrow',
                '\\\\cup', '\\\\cap',
                '\\\\oplus', '\\\\otimes',
                '\\\\wedge', '\\\\vee', '\\\\circ',
                '\\\\leq', '\\\\geq', '\\\\neq',
            ];
            const opAlt = OP_PIECES.join('|');

            // OP_LEFT:  does the text to the LEFT of the ellipsis end with an operator?
            //           Allow arbitrary whitespace/subscripts between operator and dots.
            const OP_LEFT = new RegExp(`(?:${opAlt})\\s*$`);

            // OP_RIGHT: does the text to the RIGHT of the ellipsis start with an operator?
            const OP_RIGHT = new RegExp(`^\\s*(?:${opAlt})`);

            // ── Ellipsis token pattern ────────────────────────────────────────────
            // Matches the three forms of misplaced ellipsis:
            //   \ldots  — baseline dots (correct in comma lists, wrong between operators)
            //   \vdots  — vertical dots (always wrong between horizontal operators)
            //   ...     — literal three dots (should almost always be \ldots or \cdots)
            //             negative lookbehind/ahead prevents matching .... or ..
            //
            // NOTE: \cdots is intentionally NOT matched — if \cdots is already used
            // between operators, it is correct and needs no suggestion.
            const ELLIPSIS_PAT = /\\ldots|\\vdots|(?<!\.)\.\.\.(?!\.)/g;

            // ── Main loop ─────────────────────────────────────────────────────────
            for (const seg of segments) {
                ELLIPSIS_PAT.lastIndex = 0;
                let m;

                while ((m = ELLIPSIS_PAT.exec(seg.text)) !== null) {
                    const token = m[0];         // e.g. "\ldots", "\vdots", "..."
                    const localIdx = m.index;

                    // Text on each side of the token (within this segment)
                    const leftPart = seg.text.slice(0, localIdx);
                    const rightPart = seg.text.slice(localIdx + token.length);

                    // ── Rule: flag only when surrounded by operators on BOTH sides ──
                    // Left side must end with an operator, right side must start with one.
                    // This covers: a + \ldots + b (anchor \ldots → \cdots)
                    // But NOT:     1, \ldots, n   (commas are not operators — skip)
                    // And NOT:     See also...    (no operators at all — skip)
                    if (!OP_LEFT.test(leftPart) || !OP_RIGHT.test(rightPart)) continue;

                    // ── Already \cdots between operators → no anchor needed ────────
                    // (handled by not matching \cdots in ELLIPSIS_PAT at all)

                    // ── Emit the suggestion ───────────────────────────────────────
                    // originalText = the misplaced token (e.g. "\ldots")
                    // suggestionText = \cdots
                    // localIdx = segment-relative offset → seg.emit handles absIndex
                    seg.emit(
                        token,          // e.g. "\ldots"
                        '\\cdots',      // always \cdots (centered dots between operators)
                        localIdx,
                        this.description
                    );
                }
            }

            return staleIds;
        },
    },

    // UndefinedReferences: {
    //     name: "Undefined References",
    //     description: "Detect \\ref, \\eqref, and \\cite with empty or undefined labels",
    //     markerClass: "ace-undefined-ref",
    //     beforeImage: "/images/description/UndefinedReferencesBefore.png",
    //     afterImage: "/images/description/UndefinedReferencesAfter.png",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();

    //         // Collect defined labels and bibitems 
    //         const definedLabels = new Set();
    //         let match;
    //         const labelPattern = /\\label\{([^}]+)\}/g;
    //         while ((match = labelPattern.exec(texFile)) !== null) {
    //             if (match[1].trim()) definedLabels.add(match[1].trim());
    //         }
    //         const bibitemPattern = /\\bibitem\{([^}]+)\}/g;
    //         while ((match = bibitemPattern.exec(texFile)) !== null) {
    //             if (match[1].trim()) definedLabels.add(match[1].trim());
    //         }

    //         function findReferences(text, command) {
    //             const results = [];
    //             const regex = new RegExp(`\\\\${command}\\{`, 'g');
    //             let m;
    //             while ((m = regex.exec(text)) !== null) {
    //                 const refStart = m.index;
    //                 let i = m.index + m[0].length;
    //                 let depth = 1;
    //                 while (i < text.length && depth > 0) {
    //                     if (text[i] === '\\') { i += 2; continue; }
    //                     if (text[i] === '{') depth++;
    //                     else if (text[i] === '}') depth--;
    //                     i++;
    //                 }
    //                 if (depth === 0) {
    //                     results.push({
    //                         start: refStart,
    //                         fullMatch: text.slice(refStart, i),
    //                         labelContent: text.slice(m.index + m[0].length, i - 1).trim(),
    //                         command
    //                     });
    //                 }
    //             }
    //             return results;
    //         }

    //         const tryAnchor = (fullMatch, position, description) => {
    //             if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
    //             }
    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(session, Range, position, fullMatch, '',
    //                     this.markerClass, this.name, description, anchorlist);
    //             }
    //         };

    //         for (const cmd of ['ref', 'eqref']) {
    //             for (const { start, fullMatch, labelContent, command } of findReferences(texFile, cmd)) {
    //                 const position = getPositionFromIndex(texFile, start, fullMatch.length);
    //                 if (!labelContent) {
    //                     tryAnchor(fullMatch, position, `Empty \\${command}: label is missing`);
    //                 } else if (!definedLabels.has(labelContent)) {
    //                     tryAnchor(fullMatch, position, `Undefined \\${command}: label "${labelContent}" not 
    // found`);
    //                 }
    //             }
    //         }

    //         for (const { start, fullMatch, labelContent, command } of findReferences(texFile, 'cite')) {
    //             const position = getPositionFromIndex(texFile, start, fullMatch.length);
    //             if (!labelContent) {
    //                 tryAnchor(fullMatch, position, `Empty \\${command}: citation key is missing`);
    //             } else {
    //                 const undefined_ = labelContent.split(',').map(l => l.trim()).filter(l => l &&
    //                     !definedLabels.has(l));
    //                 if (undefined_.length > 0) {
    //                     const desc = undefined_.length === 1
    //                         ? `Undefined \\${command}: citation key "${undefined_[0]}" not found`
    //                         : `Undefined \\${command}: citation keys not found: ${undefined_.join(', ')}`;
    //                     tryAnchor(fullMatch, position, desc);
    //                 }
    //             }
    //         }

    //         return staleIds;
    //     }
    // },

    DuplicateLabels: {
        name: "Duplicate Labels",
        description: "Detect duplicate \\label definitions",
        markerClass: "ace-duplicate-label",
        beforeImage: "/images/description/DuplicateLabelsBefore.png",
        afterImage: "/images/description/DuplicateLabelsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function findLabels(text) {
                const results = [];
                const regex = /\\label\{/g;
                let m;
                while ((m = regex.exec(text)) !== null) {
                    if (LineCommented(texFile, m.index)) continue;
                    let i = m.index + m[0].length;
                    let depth = 1;
                    while (i < text.length && depth > 0) {
                        if (text[i] === '\\') { i += 2; continue; }
                        if (text[i] === '{') depth++;
                        else if (text[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const labelName = text.slice(m.index + m[0].length, i - 1).trim();
                        if (labelName) results.push({
                            start: m.index, fullMatch: text.slice(m.index, i),
                            labelName
                        });
                    }
                }
                return results;
            }

            const labelOccurrences = {};
            for (const label of findLabels(texFile)) {
                if (!labelOccurrences[label.labelName]) labelOccurrences[label.labelName] = [];
                labelOccurrences[label.labelName].push(label);
            }

            for (const [labelName, occurrences] of Object.entries(labelOccurrences)) {
                if (occurrences.length <= 1) continue;
                occurrences.forEach((occ, index) => {
                    const position = getPositionFromIndex(texFile, occ.start, occ.fullMatch.length);
                    const description = index === 0
                        ? `Duplicate label "${labelName}" (first occurrence, ${occurrences.length - 1} more 
    found)`
                        : `Duplicate label "${labelName}" (occurrence ${index + 1} of 
    ${occurrences.length})`;

                    if (isRejectedByLivePosition(position, this.name, occ.fullMatch)) return;
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                    }
                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, occ.fullMatch, '',
                            this.markerClass, this.name, description, anchorlist);
                    }
                });
            }

            return staleIds;
        }
    },


    //no before and after
    NonSequentialReferences: {
        name: "Non-Sequential References",
        description: "Detect figures/tables referenced out of sequential order",
        markerClass: "ace-non-sequential-ref",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const isCommented = (text, index) => {
                let lineStart = index;
                while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
                const linePrefix = text.slice(lineStart, index);
                for (let i = 0; i < linePrefix.length; i++) {
                    if (linePrefix[i] === '%' && (i === 0 || linePrefix[i - 1] !== '\\')) return true;
                }
                return false;
            };

            const labelInfo = {};
            let labelCounter = 0;
            const envRegex = /\\begin\{(table|figure)\*?\}([\s\S]*?)\\end\{\1\*?\}/g;
            let envMatch;

            while ((envMatch = envRegex.exec(texFile)) !== null) {
                const envType = envMatch[1];
                const envContent = envMatch[2];
                const envStartIndex = envMatch.index + envMatch[0].indexOf(envMatch[2]);
                const labelRegex = /\\label\{/g;
                let labelMatch;

                while ((labelMatch = labelRegex.exec(envContent)) !== null) {
                    const absoluteIndex = envStartIndex + labelMatch.index;
                    if (isCommented(texFile, absoluteIndex)) continue;

                    let i = labelMatch.index + labelMatch[0].length;
                    let depth = 1;
                    while (i < envContent.length && depth > 0) {
                        if (envContent[i] === '{') depth++;
                        else if (envContent[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const labelName = envContent.slice(labelMatch.index + labelMatch[0].length, i -
                            1).trim();
                        if (labelName && !labelInfo.hasOwnProperty(labelName)) {
                            labelInfo[labelName] = { order: labelCounter++, type: envType };
                        }
                    }
                }
            }

            const lastSeenOrder = { figure: -1, table: -1 };
            const refRegex = /\\ref\{([^}]+)\}/g;
            let refMatch;

            while ((refMatch = refRegex.exec(texFile)) !== null) {
                const refLabel = refMatch[1].trim();
                const fullMatch = refMatch[0];
                if (isCommented(texFile, refMatch.index)) continue;
                if (!labelInfo.hasOwnProperty(refLabel)) continue;

                const { order: currentOrder, type: refType } = labelInfo[refLabel];
                const lastOrder = lastSeenOrder[refType];

                if (currentOrder < lastOrder) {
                    const prevLabelName = Object.entries(labelInfo).find(
                        ([, info]) => info.type === refType && info.order === lastOrder
                    )?.[0] ?? 'unknown';
                    const typeLabel = refType === 'figure' ? 'Figure' : 'Table';
                    const description = `${typeLabel} "${refLabel}" is cited out of sequential order. 
    Previously cited "${prevLabelName}" which appears later in the document.`;
                    const position = getPositionFromIndex(texFile, refMatch.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) {
                        lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
                        continue;
                    }
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                            lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
                            continue;
                        }
                    }
                    createAnchor(session, Range, position, fullMatch, '',
                        this.markerClass, this.name, description, anchorlist);
                }

                lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
            }

            return staleIds;
        }
    },

    //no before and after
    MathCommaFix: {
        name: "Math Comma Fix",
        description: "Move comma outside of inline math expressions like $abc,$ → $abc$,",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const regex = /\$(.+?),\$/g;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `$${match[1]}$,`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, "Move comma outside of inline math", anchorlist);
                }
            }

            return staleIds;
        }
    },

    FigureRef: {
        name: "Convert FigureRef",
        description: "Convert Figure \\ref{fig1}a to Figure \\ref{fig1}(a)",
        markerClass: "ace-Common-highlight",


        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const regex = /Figure\s*\\ref\{([^}]*)\}([\w])(?!\w)/gi;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `Figure \\ref{${match[1]}}(${match[2]})`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, "Figure Ref Rechange", anchorlist);
                }
            }

            return staleIds;
        }
    },
    //no before and after
    UnusedReferenceFinding: {
        name: "Unused References",
        description: "Finding \\ref{} that have no matching \\label anywhere",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();
            const self = this;

            // ── STEP 1: CLEAN SWEEP ───────────────────────────────────────────────
            existingSuggestions.forEach(s => {
                if (s.styleInfo?.name === self.name) staleIds.add(s.id);
            });

            // ── STEP 2: Build context ─────────────────────────────────────────────
            const ctx = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                scope: { wholeDocument: true },
                excludeMath: false,
                mode: 'query',
                overlapMode: 'refresh',
            });

            // ── STEP 3: Collect ALL \label keys from the ENTIRE file ──────────────
            const definedLabels = new Set();
            const labelPat = /\\label\{([^}]*)\}/g;
            let m;
            while ((m = labelPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;
                m[1].split(',').forEach(k => definedLabels.add(k.trim()));
            }

            // ── STEP 4: Scan \ref{} and \eqref{} in the document body ────────────
            // Use ctx.segments (whole document scope) to iterate only non-comment text.
            // For each unmatched ref, call ctx.emitQuery with the ABSOLUTE index
            // (seg.absStart + localOffset) — same as AffiliationCheck uses author.absIndex.
            const refPat = /\\(?:ref|eqref)\{([^}]*)\}/g;

            for (const seg of ctx.segments) {
                refPat.lastIndex = 0;
                while ((m = refPat.exec(seg.text)) !== null) {
                    const absIndex = seg.absStart + m.index;   // ABSOLUTE position
                    if (isCommented(texFile, absIndex)) continue;

                    const key = m[1].trim();
                    const fullMatch = m[0];   // e.g. \ref{fig:results}

                    if (definedLabels.has(key)) {
                        // Ref HAS a matching label → was swept stale → let it disappear.
                        continue;
                    }

                    // Ref has NO matching label → emit query with absolute index.
                    // ctx.emitQuery (not seg.emitQuery) so we control the absIndex
                    // and the pattern exactly mirrors AffiliationCheck.
                    ctx.emitQuery(
                        fullMatch,
                        `reference "${key}" has no matching \\label{${key}} — add the label or fix the key`,
                        absIndex,       // ← absolute, same as AffiliationCheck's author.absIndex
                        `Reference [${key}] is not cited in the text. Kindly check and provide the appropriate citation.`
                    );
                }
            }

            return staleIds;
        },
    },

    //no before and after

    UnusedLabelsFinding: {
        name: "Unused Labels",
        description: "Finding \\label{} inside tables and figures that have no matching \\ref",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();
            const self = this;

            // ── STEP 1: CLEAN SWEEP ───────────────────────────────────────────────
            // Mark ALL existing anchors for this style as stale.
            // ctx.emitQuery will rescue any that still have an active error.
            existingSuggestions.forEach(s => {
                if (s.styleInfo?.name === self.name) staleIds.add(s.id);
            });

            // ── STEP 2: Build context for the query emitter ───────────────────────
            const ctx = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                scope: {},
                excludeMath: false,
                mode: 'query',
                overlapMode: 'refresh',
            });

            // ── STEP 3: Collect ALL \ref and \eqref keys from the ENTIRE document ─
            const usedRefs = new Set();
            const refPat = /\\(?:ref|eqref)\{([^}]*)\}/g;
            let m;
            while ((m = refPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;
                m[1].split(',').forEach(k => usedRefs.add(k.trim()));
            }

            // ── STEP 4: Scan \label{} inside table and figure environments ─────────
            // We search the full file but only flag labels that are inside
            // table or figure environments (same filtering as before, explicit).
            const envPat = /\\begin\{(table|figure)\*?\}([\s\S]*?)\\end\{(table|figure)\*?\}/gi;
            while ((m = envPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;
                const envBody = m[2];
                const envBodyAbs = m.index + m[0].indexOf(m[2]);
                const envName = m[1].toLowerCase();

                const labelPat = /\\label\{([^}]*)\}/g;
                let lm;
                while ((lm = labelPat.exec(envBody)) !== null) {
                    const absIndex = envBodyAbs + lm.index;
                    if (isCommented(texFile, absIndex)) continue;

                    const key = lm[1].trim();
                    const fullMatch = lm[0];   // e.g. \label{fig:results}

                    if (usedRefs.has(key)) {
                        // Label IS referenced → was swept stale → let it disappear.
                        // Do NOT rescue — no error exists.
                        continue;
                    }

                    // Label is NOT referenced → emit query using ctx (absolute index).
                    // AffiliationCheck uses ctx.emitQuery with absolute absIndex.
                    // The rescue logic in buildQueryOnlyEmitter will:
                    //   - find the existing anchor at the same position (Ace tracked)
                    //   - match queryMessage → staleIds.delete → anchor stays ✓
                    //   - if \AQ already above → skip create (but anchor was already rescued)
                    ctx.emitQuery(
                        fullMatch,
                        `label "${key}" is never cited — add \\ref{${key}} somewhere or remove this label`,
                        absIndex,
                        `Label "${key}" inside ${envName} has no matching \\ref`
                    );
                }
            }

            return staleIds;
        },
    },

    //no before and after
    UnusedCiteFinding: {
        name: "Unused Cite finding",
        description: "Finding \\cite{} calls that have no matching \\bibitem{} in the project",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            if (!texFile) return new Set();
            const staleIds = new Set();
            const self = this;


            existingSuggestions.forEach(s => {
                if (s.styleInfo?.name === self.name) staleIds.add(s.id);
            });


            const ctx = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                scope: { wholeDocument: true },
                excludeMath: false,
                mode: 'query',
                overlapMode: 'refresh',
            });


            const definedBibs = new Set();

            const bibPat = /\\bibitem(?:\[[^\]]*\])?\{([^}]*)\}/g;
            let m;
            while ((m = bibPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;

                m[1].split(',').forEach(k => definedBibs.add(k.trim()));
            }


            const citePattern = /\\(?:cite|citealp)(?:\[[\s\S]*?\])?\{/gi;

            for (const seg of ctx.segments) {
                citePattern.lastIndex = 0;
                let n;

                while ((n = citePattern.exec(seg.text)) !== null) {
                    const absIndex = seg.absStart + n.index;
                    if (isCommented(texFile, absIndex)) continue;


                    let depth = 1;
                    let content = "";
                    let i = citePattern.lastIndex;

                    while (i < seg.text.length && depth !== 0) {
                        const char = seg.text[i];
                        if (char === "{") depth++;
                        else if (char === "}") depth--;
                        if (depth !== 0) content += char;
                        i++;
                    }


                    const fullMatch = seg.text.substring(n.index, i);
                    const keys = content.split(',').map(k => k.trim());


                    for (const key of keys) {
                        if (key && !definedBibs.has(key)) {

                            ctx.emitQuery(
                                fullMatch,
                                `Citation "${key}" has no matching \\bibitem{${key}} — please check the bibliography.`,
                                absIndex,
                                `Reference [${key}] is not cited in the bibliography. Kindly check and provide the appropriate bibitem.`
                            );
                            break;
                        }
                    }
                }
            }

            return staleIds;
        },
    },

    UnusedBibitemFinding: {
        name: "Unused Bibliography Entries",
        description: "Finding \\bibitem{} entries that are not referenced by any \\cite{} call",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            if (!texFile) return new Set();
            const staleIds = new Set();
            const self = this;

            existingSuggestions.forEach(s => {
                if (s.styleInfo?.name === self.name) staleIds.add(s.id);
            });

            const ctx = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                scope: { wholeDocument: true },
                excludeMath: false,
                mode: 'query',
                overlapMode: 'refresh',
            });
            const usedKeys = new Set();
            const citePattern = /\\(?:cite|citealp)(?:\[[\s\S]*?\])?\{([^}]*)\}/gi;
            let c;
            while ((c = citePattern.exec(texFile)) !== null) {
                if (isCommented(texFile, c.index)) continue;
                c[1].split(',').forEach(k => usedKeys.add(k.trim()));
            }
            const bibPat = /\\bibitem(?:\[[^\]]*\])?\{([^}]*)\}/g;
            let m;
            while ((m = bibPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;

                const fullMatch = m[0];
                const bibKeys = m[1].split(',').map(k => k.trim());
                const absIndex = m.index;

                for (const key of bibKeys) {
                    if (key && !usedKeys.has(key)) {
                        ctx.emitQuery(
                            fullMatch,
                            `Bibliography entry "${key}" is never cited in the document.`,
                            absIndex,
                            `The bibitem [${key}] is defined but not used. Please check if this reference is necessary or if the citation is missing.`
                        );
                    }
                }
            }

            return staleIds;
        },
    },

    // UnusedCiteFinding: {
    //     name: "Missing Cite Entries",
    //     description: "Finding \\cite{} calls that have no matching \\bibitem{} in the project",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
    //         if (!texFile) return new Set();
    //         const staleIds = new Set();
    //         const self = this;

    //         // 1. Build the list of VALID bibitems first (Pass 1)
    //         const allbibs = new Set();
    //         // This regex finds \bibitem{key} or \bibitem[optional]{key}
    //         const bibpattern = /\\bibitem(?:\[[^\]]*\])?\{([^}]+)\}/gi;
    //         let bMatch;
    //         while ((bMatch = bibpattern.exec(texFile)) !== null) {
    //             if (bMatch[1]) allbibs.add(bMatch[1].trim());
    //         }

    //         // 2. Build Style Context for the segments (Pass 2)
    //         const ctx = buildStyleContext({
    //             texFile, session, Range, anchorlist,
    //             existingSuggestions, staleIds,
    //             styleName: self.name,
    //             markerClass: self.markerClass,
    //             scope: { wholeDocument: true },
    //             mode: 'query',
    //         });

    //         const citepattern = /\\cite(?:\[([\s\S]*?)\])?\{/gi;

    //         for (let seg of ctx.segments) {
    //             citepattern.lastIndex = 0;
    //             let m;

    //             while ((m = citepattern.exec(seg.text)) !== null) {
    //                 let depth = 1;
    //                 let content = "";
    //                 let i = citepattern.lastIndex;

    //                 // Balanced brace logic to get keys
    //                 while (i < seg.text.length && depth !== 0) {
    //                     const char = seg.text[i];
    //                     if (char === "{") depth++;
    //                     else if (char === "}") depth--;
    //                     if (depth !== 0) content += char;
    //                     i++;
    //                 }

    //                 const keys = content.split(',').map(k => k.trim());

    //                 // 3. COMPARE: For each key in \cite{}, check if it's in \bibitem{}
    //                 keys.forEach(key => {
    //                     if (key && !allbibs.has(key)) {
    //                         // This is a missing citation!
    //                         const fullMatch = seg.text.substring(m.index, i);

    //                         seg.emit(
    //                             fullMatch,
    //                             '', // No suggestion for a missing key
    //                             m.index,
    //                             `\\AQ{The citation key "${key}" was not found in the bibliography (\\bibitem).}`
    //                         );
    //                     }
    //                 });
    //             }
    //         }

    //         return staleIds;
    //     }
    // },




    //no before and after
    authornamecheck: {
        name: "Author Name Checking",
        description: "A query to check the author name is correctly formatted or not",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\\author\{/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;

                const position = getPositionFromIndex(texFile, match.index, match[0].length);

                if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                const prevLine = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" : "";
                if (!prevLine.trim().startsWith("\\AQ")) {
                    anchorlist.push({
                        id: `${this.name}__${crypto.randomUUID()}`,
                        startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                        endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                        markerId: null,
                        originalText: null,
                        suggestionText: null,
                        queryOnly: true,
                        styleInfo: {
                            name: "Author Name Checking",
                            description: `Please check all author names, initials, and affiliations to confirm 
    they are accurate.`
                        }
                    });
                }
            }

            return staleIds;
        }
    },

    // EquationEndCapitalization: {
    //     name: "Equation End Capitalization",
    //     description: "Ensure the first letter after \\end{equation} is capitalized",
    //     markerClass: "ace-Common-highlight",
    //     beforeImage: "/images/description/EndEquationBefore.png",
    //     afterImage: "/images/description/EndEquationAfter.png",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const pattern = /\\end\{equation\}\s*\n\s*(?:\\\w+\{)?([a-zA-Z])/g;
    //         let match;

    //         while ((match = pattern.exec(texFile)) !== null) {
    //             const fullMatch = match[0];
    //             const firstLetter = match[1];
    //             if (firstLetter === firstLetter.toUpperCase()) continue;

    //             const suggestion = fullMatch.replace(firstLetter, firstLetter.toUpperCase());
    //             const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

    //             if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //             }

    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(session, Range, position, fullMatch, suggestion,
    //                     this.markerClass, this.name, this.description, anchorlist);
    //             }
    //         }

    //         return staleIds;
    //     }
    // },



    // EquationEndCapitalization: {
    //     name: "Equation End Capitalization",
    //     description: "Ensure the first letter after \\end{equation} is capitalized",
    //     markerClass: "ace-Common-highlight",
    //     beforeImage: "/images/description/EndEquationBefore.png",
    //     afterImage: "/images/description/EndEquationAfter.png",

    //     execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
    //         if (!texFile) return new Set();
    //         const staleIds = new Set();
    //         const self = this;


    //         existingSuggestions.forEach(s => {
    //             if (s.styleInfo?.name === self.name) staleIds.add(s.id);
    //         });


    //         const ctx = buildStyleContext({
    //             texFile, session, Range, anchorlist,
    //             existingSuggestions, staleIds,
    //             styleName: self.name,
    //             markerClass: self.markerClass,
    //             scope: { wholeDocument: true },
    //             excludeMath: false,
    //             mode: 'query',
    //             overlapMode: 'refresh',
    //         });



    //         let endequationpattern = /\\end\{equation\}/gi;
    //         let m;
    //         for (const seg of ctx.segments) {
    //             endequationpattern.lastIndex = 0;
    //             while ((m = endequationpattern.exec(seg.text)) !== null) {
    //                 console.log("end of the equations are:", m[0]);
    //             }
    //         }
    //         return staleIds;
    //     },
    // },

    //no before and after
    TableNumericAlignment: {
        name: "Table Numeric Alignment",
        description: "Apply right/decimal align to numeric table columns",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const isInsideCommentedTable = (text, tabularStart) => {
                const beforeTabular = text.slice(0, tabularStart);
                const tableBeginPattern = /\\begin\{table\*?\}/g;
                let lastTableBegin = null, match;
                while ((match = tableBeginPattern.exec(beforeTabular)) !== null) {
                    lastTableBegin = { index: match.index, text: match[0] };
                }
                if (!lastTableBegin) return false;
                const betweenText = text.slice(lastTableBegin.index, tabularStart);
                if (/\\end\{table\*?\}/.test(betweenText)) return false;
                if (isLineCommented(text, lastTableBegin.index)) return true;
                const afterTabular = text.slice(tabularStart);
                const endTableMatch = afterTabular.match(/\\end\{table\*?\}/);
                if (endTableMatch && isLineCommented(text, tabularStart + endTableMatch.index))
                    return true;
                return false;
            };

            const isNumericCell = (cell) => {
                let clean = cell
                    .replace(/\\hline/g, '').replace(/\\cline\{[^}]*\}/g, '')
                    .replace(/\\multicolumn\{[^}]*\}\{[^}]*\}\{([^}]*)\}/g, '$1')
                    .replace(/\\textbf\{([^}]*)\}/g, '$1').replace(/\\textit\{([^}]*)\}/g, '$1')
                    .replace(/\\text\{([^}]*)\}/g, '$1').replace(/\$([^$]*)\$/g, '$1')
                    .replace(/\\[a-zA-Z]+\s*/g, '').replace(/[{}]/g, '').replace(/\s+/g, '').trim();

                if (clean === '' || clean === '-' || clean === '--' || clean === '---')
                    return { skip: true, isNumeric: false, hasDecimal: false };

                const isInteger = /^[+-]?\d+$/.test(clean);
                const isDecimal = /^[+-]?\d+\.\d+$/.test(clean);
                const isScientific = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(clean);
                const isNumeric = isInteger || isDecimal || isScientific;
                return { skip: false, isNumeric, hasDecimal: isDecimal || isScientific };
            };

            const tabularEnvs = findTabularEnvs(texFile);

            for (const { start, fullMatch, envName, columnSpec, tableBody } of tabularEnvs) {
                if (isInsideCommentedTable(texFile, start)) continue;

                const endPattern = new RegExp(`\\\\end\\{${envName}\\*?\\}`);
                const endMatch = fullMatch.match(endPattern);
                if (!endMatch) continue;

                const endIndex = start + fullMatch.lastIndexOf(endMatch[0]);
                if (isLineCommented(texFile, start) || isLineCommented(texFile, endIndex)) continue;

                const specParts = parseColumnSpec(columnSpec);
                const dataColIndices = specParts.map((p, idx) => p.isDataCol ? idx : -1).filter(idx => idx
                    !== -1);
                if (dataColIndices.length === 0) continue;

                const numDataCols = dataColIndices.length;
                const rawRows = tableBody.split(/\\\\(?:\[[^\]]*\])?/);
                const parsedRows = rawRows
                    .map(row => {
                        const clean = row
                            .replace(/\\hline|\\cline\{[^}]*\}|\\toprule|\\midrule|\\bottomrule/g, '')
                            .trim();
                        return clean ? clean.split('&').map(c => c.trim()) : null;
                    })
                    .filter(row => row !== null && row.length > 1);

                if (parsedRows.length === 0) continue;

                const startRow = parsedRows[0].every(cell => {
                    const { isNumeric, skip } = isNumericCell(cell);
                    return !isNumeric && !skip;
                }) ? 1 : 0;

                const colStats = Array.from({ length: numDataCols }, () => ({
                    totalCells: 0, numericCells:
                        0, decimalCells: 0
                }));

                for (let rowIdx = startRow; rowIdx < parsedRows.length; rowIdx++) {
                    parsedRows[rowIdx].forEach((cell, cellIdx) => {
                        if (cellIdx >= numDataCols) return;
                        const result = isNumericCell(cell);
                        if (!result.skip) {
                            colStats[cellIdx].totalCells++;
                            if (result.isNumeric) {
                                colStats[cellIdx].numericCells++;
                                if (result.hasDecimal) colStats[cellIdx].decimalCells++;
                            }
                        }
                    });
                }

                const newSpecParts = specParts.map(p => ({ ...p }));
                let needsChange = false;

                dataColIndices.forEach((specIdx, dataColPos) => {
                    const stats = colStats[dataColPos];
                    if (stats.totalCells === 0 || stats.numericCells !== stats.totalCells) return;
                    const currentAlign = specParts[specIdx].align || specParts[specIdx].char;
                    const targetAlign = stats.decimalCells > 0 ? 'S' : 'r';
                    if (currentAlign !== targetAlign) {
                        newSpecParts[specIdx].char = targetAlign;
                        needsChange = true;
                    }
                });

                if (!needsChange) continue;

                const newColumnSpec = newSpecParts.map(p => p.char).join('');

                // Rejection / duplicate check on the column spec position 
                const specIndex = texFile.indexOf(columnSpec, start);
                if (specIndex === -1) continue;
                const position = getPositionFromIndex(texFile, specIndex, columnSpec.length);

                if (isRejectedByLivePosition(position, this.name, columnSpec)) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }
                createGranularAnchors(session, Range, texFile, start, fullMatch,
                    columnSpec, newColumnSpec, this.markerClass, this.name, anchorlist);
            }
            return staleIds;
        }
    },

    ConvertUStoUK: {
        name: "Convert US Word to UK Word",
        description: "Convert US spelling to UK English",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();

            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                overlapMode: 'simple',
            });

            // 1. Get all words from your imported object
            const usWords = Object.keys(US_TO_UK);
            if (usWords.length === 0) return staleIds;

            // 2. Create Regex dynamically
            const wordPat = new RegExp(`\\b(${usWords.join('|')})\\b`, 'gi');

            let match;
            while ((match = wordPat.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;

                const originalWord = match[0];
                const replacement = US_TO_UK[originalWord.toLowerCase()];

                if (replacement) {
                    // Case preservation: (e.g., Color -> Colour)
                    const suggestedWord = (originalWord[0] === originalWord[0].toUpperCase())
                        ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
                        : replacement;

                    if (originalWord.toLowerCase() === suggestedWord.toLowerCase()) continue;

                    emit(
                        originalWord,
                        suggestedWord,
                        match.index,
                        this.description
                    );
                }
            }
            return staleIds;
        }
    },



    ConvertUKtoUS: {
        name: "Convert UK Word to US Word",
        description: "Convert UK spelling to US English",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();

            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                overlapMode: 'simple',
            });

            const ukWords = Object.keys(UK_TO_US);
            const wordPat = new RegExp(`\\b(${ukWords.join('|')})\\b`, 'gi');

            let match;
            while ((match = wordPat.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;

                const originalWord = match[0];
                const replacement = UK_TO_US[originalWord.toLowerCase()];

                if (replacement) {
                    const suggestedWord = (originalWord[0] === originalWord[0].toUpperCase())
                        ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
                        : replacement;

                    if (originalWord.toLowerCase() === suggestedWord.toLowerCase()) continue;

                    emit(
                        originalWord,
                        suggestedWord,
                        match.index,
                        this.description
                    );
                }
            }
            return staleIds;
        }
    },

    ConvertToMajoritySpelling: {
        name: "Convert To Majority Spelling",
        description: "Flag minority-spelling words to match the document's majority spelling convention.",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();

            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                overlapMode: 'simple',
            });

            // ── Step 1: Build unified pair map ────────────────────────────────────
            // For every US word, find its UK equivalent and vice versa.
            // Result: Set of canonical pairs { us: "color", uk: "colour" }
            const pairs = new Map(); // key = us word, value = uk word

            for (const [usWord, ukWord] of Object.entries(US_TO_UK)) {
                pairs.set(usWord.toLowerCase(), ukWord.toLowerCase());
            }

            // Also catch any UK→US entries not already covered
            for (const [ukWord, usWord] of Object.entries(UK_TO_US)) {
                const us = usWord.toLowerCase();
                const uk = ukWord.toLowerCase();
                if (!pairs.has(us)) {
                    pairs.set(us, uk);
                }
            }

            // ── Step 2: Count occurrences of every word in the document ──────────
            // Single pass over the full text — count all relevant words at once
            const allVariants = new Set();
            for (const [us, uk] of pairs) {
                allVariants.add(us);
                allVariants.add(uk);
            }

            const countPat = new RegExp(
                `\\b(${[...allVariants].join('|')})\\b`, 'gi'
            );

            const counts = new Map(); // word (lowercase) → count
            let cm;
            countPat.lastIndex = 0;

            while ((cm = countPat.exec(texFile)) !== null) {
                // Skip commented lines — don't count words in comments
                if (isCommented(texFile, cm.index)) continue;
                const w = cm[0].toLowerCase();
                counts.set(w, (counts.get(w) ?? 0) + 1);
            }

            // ── Step 3: Decide majority for each pair ─────────────────────────────
            // minority → majority mapping (what to flag and what to change to)
            // e.g. { "colour": "color" }  means flag "colour", suggest "color"
            const flagAs = new Map(); // minority word → majority word

            for (const [us, uk] of pairs) {
                const usCount = counts.get(us) ?? 0;
                const ukCount = counts.get(uk) ?? 0;

                if (usCount === 0 && ukCount === 0) continue; // neither present
                if (usCount === ukCount) continue;             // tie — no clear majority

                if (usCount > ukCount) {
                    // US is majority → flag UK words → suggest US
                    flagAs.set(uk, us);
                } else {
                    // UK is majority → flag US words → suggest UK
                    flagAs.set(us, uk);
                }
            }

            if (flagAs.size === 0) return staleIds;

            // ── Step 4: Emit suggestions for every minority occurrence ────────────
            const flagPat = new RegExp(
                `\\b(${[...flagAs.keys()].join('|')})\\b`, 'gi'
            );

            let match;
            flagPat.lastIndex = 0;

            while ((match = flagPat.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;

                const originalWord = match[0];
                const majority = flagAs.get(originalWord.toLowerCase());
                if (!majority) continue;

                // Preserve original capitalisation
                const suggestedWord = originalWord[0] === originalWord[0].toUpperCase()
                    ? majority.charAt(0).toUpperCase() + majority.slice(1)
                    : majority;

                if (originalWord.toLowerCase() === suggestedWord.toLowerCase()) continue;

                emit(originalWord, suggestedWord, match.index, this.description);
            }

            return staleIds;
        }
    },


}
